import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const toPoLine = (m) => ({ item_id: m.item_id, sku: m.sku, name: m.name, unit: m.unit, ordered_qty: Math.ceil(m.shortage) });

function groupBySupplier(shortages) {
  const groups = {};
  shortages.forEach((m) => {
    const key = m.supplier || "UNSPECIFIED";
    (groups[key] = groups[key] || []).push(m);
  });
  return groups;
}

// Explodes a product's BOM for a target quantity, tracks which short materials have been ordered,
// and exposes single/bulk PO raising. Used by MaterialShortageDialog.
export function useMaterialCheck({ product, facility, onCreatePO, onBulkPO }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordered, setOrdered] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/bom/material-check?item_id=${product.item_id}&quantity=${product.quantity}&facility=${encodeURIComponent(facility)}`)
      .then((d) => { if (active) setData(d); })
      .catch((e) => logger.error("Material check failed:", e))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [product.item_id, product.quantity, facility, setData, setLoading]);

  const materials = useMemo(() => data?.materials || [], [data]);
  const shortages = useMemo(() => materials.filter((m) => m.shortage > 0), [materials]);
  // Manufactured spares are built in-house (Work Orders tab), never purchased.
  const buyableShortages = useMemo(() => shortages.filter((m) => m.item_type !== "mfg_spare"), [shortages]);
  const allOrdered = buyableShortages.length > 0 && buyableShortages.every((m) => ordered[m.item_id]);

  const raisePO = async (m) => {
    try {
      await onCreatePO({ item_id: m.item_id, sku: m.sku, name: m.name, unit: m.unit, suggested_qty: Math.ceil(m.shortage), supplier: m.supplier, lead_time_days: m.lead_time_days });
      setOrdered((p) => ({ ...p, [m.item_id]: true }));
    } catch (err) {
      logger.error("Create PO from shortage failed (toast shown by action; row left un-ordered for retry):", err);
    }
  };

  const orderAllMissing = async () => {
    for (const [supplier, ms] of Object.entries(groupBySupplier(buyableShortages))) {
      await onBulkPO(supplier, ms.map(toPoLine), Math.max(0, ...ms.map((m) => m.lead_time_days || 0)));
    }
    setOrdered(Object.fromEntries(buyableShortages.map((m) => [m.item_id, true])));
  };

  return { materials, shortages, loading, ordered, allOrdered, raisePO, orderAllMissing };
}
